import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import BrandMetricsGrid from '@/components/national-team/BrandMetricsGrid';
import CycleProgress from '@/components/national-team/CycleProgress';
import CycleEvolutionChart from '@/components/national-team/CycleEvolutionChart';
import DigitalPresence from '@/components/national-team/DigitalPresence';
import StarPlayers from '@/components/national-team/StarPlayers';
import { useDemoMode } from '@/components/common/DemoModeToggle';
import { DataIntegrityBadge, LastUpdated, PlatformFooter } from '@/components/common/PageElements';
import {
  SectionBlock,
  KeyGrowthDrivers,
  RiskConcentration,
  StrategicOutlook,
} from '@/components/national-team/ExecutiveElements';
import { generatePDFReport } from '@/services/pdfExportService';
import { DashboardTabs } from '@/components/common/DashboardTabs';
import { FeedbackWidget } from '@/components/common/FeedbackWidget';
import { trackEvent } from '@/hooks/useAnalytics';
import { authFetch } from '@/utils/authFetch';
import {
  FileDown, Calendar, Users, BarChart3, Layers,
  ArrowRight, TrendingUp, Target,
} from 'lucide-react';

/* ═══ Page-specific tabs (no Export — relocated to controls row) ═══ */
const PAGE_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'drivers', label: 'Drivers', icon: TrendingUp },
  { id: 'opportunities', label: 'Opportunities', icon: Target },
  { id: 'scenarios', label: 'Scenarios', icon: Layers },
];

const fmt = (v) => {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(0)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v}`;
};

export default function NationalTeamDashboard() {
  const [activeTeam, setActiveTeam] = useState('usmnt');
  const [activeTab, setActiveTab] = useState('overview');
  const [teams, setTeams] = useState({ usmnt: null, uswnt: null });
  const [verifiedSocialData, setVerifiedSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDemoMode } = useDemoMode();

  // Load both teams on mount for side-by-side comparison
  useEffect(() => {
    async function loadTeams() {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          authFetch('/api/national-team/usmnt'),
          authFetch('/api/national-team/uswnt'),
        ]);
        setTeams({
          usmnt: r1.ok ? await r1.json() : null,
          uswnt: r2.ok ? await r2.json() : null,
        });
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    loadTeams();
  }, []);

  // Verified social data (for PDF export context)
  useEffect(() => {
    async function loadVerified() {
      try {
        const res = await authFetch(`/api/us-soccer/verified-social?demo_mode=${isDemoMode}`);
        if (res.ok) setVerifiedSocialData(await res.json());
      } catch (e) {
        setVerifiedSocialData(null);
      }
    }
    loadVerified();
  }, [isDemoMode]);

  const data = teams[activeTeam];

  const handleExportPDF = () => {
    if (data) {
      trackEvent('export_pdf_click', { report: 'national-team', team: activeTeam });
      generatePDFReport(data, 'national-team', {
        teamId: activeTeam,
        verifiedSocialData,
        isDemoMode,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Failed to load team data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="national-team-dashboard">
      <Header />

      {/* ── Page Header — simplified, metadata discreet ── */}
      <div className="border-b border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-foreground tracking-tight" data-testid="page-title">
                  USMNT vs USWNT — Intelligence Comparison
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Compare brand value, commercial strength, and strategic outlook across programs.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <DataIntegrityBadge status={isDemoMode ? 'simulated' : 'verified'} />
              <LastUpdated />
            </div>
          </div>
        </div>
      </div>

      {/* ── Side-by-Side Comparison ── */}
      <ComparisonBlock
        usmnt={teams.usmnt}
        uswnt={teams.uswnt}
        activeTeam={activeTeam}
        onSelect={setActiveTeam}
      />

      {/* ── Controls row: detail label + Export ── */}
      <div className="border-b border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <p className="text-xs text-muted-foreground" data-testid="detail-label">
            Detailed analysis: <span className="font-semibold text-foreground">{data.name}</span>
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            className="text-xs h-8"
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-3.5 h-3.5 mr-1.5" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* ── Tabs (no Export — moved to controls row above) ── */}
      <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={PAGE_TABS} />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10">

        {/* Overview: Strategic Outlook + Key Drivers */}
        {activeTab === 'overview' && (
          <>
            <StrategicOutlook data={data} brandMetrics={data.brand_metrics} />
            <KeyGrowthDrivers data={data} brandMetrics={data.brand_metrics} />
          </>
        )}

        {/* Drivers: Evolution chart + Cycle + Digital */}
        {activeTab === 'drivers' && (
          <>
            <SectionBlock
              icon={Calendar}
              title="4-Year Brand Evolution"
              description="Tracking brand strength across World Cup cycles."
            >
              <CycleEvolutionChart
                currentCycle={data.current_cycle}
                historicalCycles={data.historical_cycles}
                brandMetrics={data.brand_metrics}
                teamName={data.name}
              />
            </SectionBlock>
            <div className="grid lg:grid-cols-2 gap-8 mb-8">
              <CycleProgress cycle={data.current_cycle} />
              <DigitalPresence digital={data.digital_presence} />
            </div>
          </>
        )}

        {/* Opportunities: Roster + Brand Dimensions */}
        {activeTab === 'opportunities' && (
          <>
            <SectionBlock
              icon={Users}
              title="Roster Brand Power"
              description="Individual player brand contribution to team equity"
            >
              <StarPlayers roster={data.roster_brand_signals} />
            </SectionBlock>
            <SectionBlock
              icon={BarChart3}
              title="Brand Dimension Analysis"
              description="Detailed scoring across brand strength components"
            >
              <BrandMetricsGrid brand={data.brand_metrics} />
            </SectionBlock>
          </>
        )}

        {/* Scenarios: Risk Concentration */}
        {activeTab === 'scenarios' && (
          <RiskConcentration rosterData={data.roster_brand_signals?.star_players} />
        )}
      </main>

      {/* ── End CTA ── */}
      <div className="border-t border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-center">
          <p className="text-sm text-muted-foreground mb-4">Want deeper intelligence on these programs?</p>
          <Link to="/briefing">
            <Button
              className="bg-primary hover:bg-primary/90 h-10 px-6 text-sm font-semibold rounded-lg"
              data-testid="cta-request-briefing"
            >
              Request a Briefing
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <FeedbackWidget route="/national-teams" />
      </div>
      <PlatformFooter showDisclaimer={false} />
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SIDE-BY-SIDE COMPARISON BLOCK
   ════════════════════════════════════════════════════════ */
function ComparisonBlock({ usmnt, uswnt, activeTeam, onSelect }) {
  const extract = (t) => {
    if (!t) return { value: '—', valueHigh: 0, score: 0, ranking: 0, yoy: 0, scoreStr: '—', rankStr: '—', yoyStr: '—' };
    const eco = t.economic_value || {};
    return {
      value: eco.range_low ? `${fmt(eco.range_low)}–${fmt(eco.range_high)}` : '—',
      valueHigh: eco.range_high || 0,
      score: t.brand_metrics?.overall_brand_score || 0,
      ranking: t.fifa_ranking || 0,
      yoy: eco.year_over_year_change || 0,
      scoreStr: t.brand_metrics?.overall_brand_score ? `${t.brand_metrics.overall_brand_score}/100` : '—',
      rankStr: t.fifa_ranking ? `#${t.fifa_ranking}` : '—',
      yoyStr: eco.year_over_year_change ? `+${eco.year_over_year_change}%` : '—',
    };
  };

  const m1 = extract(usmnt);
  const m2 = extract(uswnt);

  const valueDelta = m1.valueHigh && m2.valueHigh ? fmt(Math.abs(m1.valueHigh - m2.valueHigh)) : null;
  const scoreDelta = m1.score && m2.score ? Math.abs(m1.score - m2.score) : null;
  const rankDelta = m1.ranking && m2.ranking ? Math.abs(m1.ranking - m2.ranking) : null;

  return (
    <div className="bg-card/10" data-testid="comparison-block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TeamCard teamId="usmnt" name="USMNT" metrics={m1} isActive={activeTeam === 'usmnt'} onSelect={() => onSelect('usmnt')} />
          <TeamCard teamId="uswnt" name="USWNT" metrics={m2} isActive={activeTeam === 'uswnt'} onSelect={() => onSelect('uswnt')} />
        </div>

        {/* Delta summary — gaps between teams */}
        {(valueDelta || scoreDelta !== null) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground/60" data-testid="comparison-deltas">
            <span className="uppercase tracking-wider font-medium">Gaps</span>
            {valueDelta && <span>Value: <span className="text-foreground/70 font-medium">{valueDelta}</span></span>}
            {scoreDelta !== null && scoreDelta > 0 && <span>Score: <span className="text-foreground/70 font-medium">{scoreDelta} pts</span></span>}
            {rankDelta !== null && rankDelta > 0 && <span>FIFA: <span className="text-foreground/70 font-medium">{rankDelta} positions</span></span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ Individual Team Card ═══ */
function TeamCard({ teamId, name, metrics, isActive, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
        isActive
          ? 'border-primary/40 bg-primary/5'
          : 'border-border/40 bg-card/40 hover:border-border/60 hover:bg-card/50'
      }`}
      data-testid={`team-card-${teamId}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-foreground tracking-tight">{name}</h3>
        {isActive && (
          <span className="text-[10px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">Viewing</span>
        )}
      </div>

      {/* Primary Metric — Brand Value */}
      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Brand Value</p>
      <p
        className={`text-xl sm:text-2xl font-bold mb-4 ${isActive ? 'text-primary' : 'text-foreground'}`}
        data-testid={`${teamId}-brand-value`}
      >
        {metrics.value}
      </p>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border/30">
        <div>
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">Score</p>
          <p className="text-sm font-semibold text-foreground">{metrics.scoreStr}</p>
        </div>
        <div>
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">FIFA</p>
          <p className="text-sm font-semibold text-foreground">{metrics.rankStr}</p>
        </div>
        <div>
          <p className="text-2xs text-muted-foreground uppercase tracking-wider">YoY</p>
          <p className="text-sm font-semibold text-stable">{metrics.yoyStr}</p>
        </div>
      </div>
    </button>
  );
}
